import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Button, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, FormHelperText, Grid
} from '@mui/material';
import '../labPractice/LabPractice.css';
import { getAllCOE } from '../../../services/adminServices/CoeServices';
import { getAllCoeLabs } from '../../../services/mentorServices/DailyPracticeServices';
import { getLearnerList } from '../../../services/mentorServices/ReviewServices';
import { toast } from 'react-toastify';
import LearnerListTable from './LearnerListTable';

const LearnerList = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selection, setSelection] = useState({ lab: '', coe: '' });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [error, setError] = useState({ lab: '', coe: '' });
  const [coes, setCoe] = useState([]);
  const [labNames, setLabNames] = useState([]);

  const handleModalToggle = () => setModalOpen(prev => !prev);

  const handleSelectionChange = (event) => {
    const { name, value } = event.target;
    setSelection(prev => ({ ...prev, [name]: value }));
    if (name === "coe") fetchLabs(value);
    setError(prev => ({ ...prev, [name]: '' }));
  };

  const fetchData = useCallback(async () => {
    const { lab, coe } = selection;
    if (!lab || !coe) {
      setError({
        lab: lab ? '' : 'Please select a Lab',
        coe: coe ? '' : 'Please select a COE',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await getLearnerList(coe, lab);
      setTableData(response.data.data || []);
      setShowTable(true);
    } catch (error) {
      setTableData([]);
      toast.error(error?.response?.data.message || error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [selection]);

  const getCoe = async () => {
    try {
      const response = await getAllCOE();
      setCoe(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load COEs");
    }
  };

  const fetchLabs = useCallback(async (coeId) => {
    try {
      const response = await getAllCoeLabs(coeId);
      setLabNames(response?.data.data || []);
    } catch (error) {
      setLabNames([]);
      setTableData([]);
      setSelection(prev => ({ ...prev, lab: '' }));
      toast.error(error?.response?.data.message || error?.message || "Failed to load labs");
    }
  }, []);

  useEffect(() => {
    getCoe();
  }, []);

  return (
    <Box className="dashboard-container" sx={{ p: { xs: 0, sm: 0, md: 0 } }}>
      <Box className="DailyPractice-header" mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={5}lg={3}>
            <FormControl fullWidth size="small" error={!!error.coe}>
              <InputLabel id="coe-select-label">COE*</InputLabel>
              <Select
                labelId="coe-select-label"
                id="coe-select"
                name="coe"
                value={selection.coe}
                onChange={handleSelectionChange}
                label="COE"
              >
                {coes.map((coe) => (
                  <MenuItem key={coe.id} value={coe.id}>{coe.name}</MenuItem>
                ))}
              </Select>
              {error.coe && <FormHelperText>{error.coe}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={5} lg={3}>
            <FormControl fullWidth size="small" error={!!error.lab}>
              <InputLabel id="lab-select-label">Lab Name*</InputLabel>
              <Select
                labelId="lab-select-label"
                id="lab-select"
                name="lab"
                value={selection.lab}
                onChange={handleSelectionChange}
                label="Lab Name"
              >
                {labNames.map((lab) => (
                  <MenuItem key={lab.id} value={lab.id}>{lab.lab_name}</MenuItem>
                ))}
              </Select>
              {error.lab && <FormHelperText>{error.lab}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={2} lg={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={fetchData}
              disabled={loading || !selection.lab || labNames.length === 0}
              sx={{ height: '100%' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'VIEW'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box className="dashboard-box-container">
        {showTable && (
          <LearnerListTable
            data={tableData}
            handleModalToggle={handleModalToggle}
            modalOpen={modalOpen}
            loading={loading}
            setLoading={setLoading}
          />
        )}
      </Box>
    </Box>
  );
};

export default LearnerList;
